import { C } from '../lib/theme';
import { notificationContactLabel } from '../state/actions/lifecycle';
import { styles } from '../lib/styles';
import { fmtDate } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { VettingSlaCell } from '../components/VettingSlaCell';
import { PagingControls } from '../components/PagingControls';
import { createCase } from '../state/createCase';
import { ApiProblem } from '../api/client';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { useCaseQuery, useDebouncedValue } from '../state/useCaseQuery';
import { getPrefs, type CaseFilter } from '../lib/prefs';
import { CustomerLabel, caseCustomerId } from '../components/CustomerLabel';
import { QueueColumnPicker, useQueueColumns } from '../components/QueueColumns';
import { isNotifiableDecision } from '../state/actions/lifecycle';
import { isNewToVetting } from '../domain/case-progress';
import { CaseStageCell } from '../components/CaseProgressTracker';
import { useCustomerDirectory } from '../state/customerDirectory';
import { useState, type ReactNode } from 'react';
import type { Case } from '../domain/initial-cases';

export function Cases() {
  const { addCase, showToast, now } = useAppData();
  const { caseSearch, setCaseSearch, openCase } = useUIState();
  const user = getCurrentUser()!;

  // #399 — the scope selector, seeded from the default-case-list-filter
  // preference. Seeded rather than bound: changing it here scopes THIS visit,
  // and the preference is what the page opens as. Making it write the
  // preference back would turn a one-off look at the whole queue into a
  // permanent setting change nobody asked for.
  const [scope, setScope] = useState<CaseFilter>(() => getPrefs().caseFilter);

  // H-2: search runs server-side over the FULL visible population — a case
  // beyond the most-recent page is findable by id/registrant/authorizer/EID.
  //
  // "Assigned to me" is a SERVER filter too, for the same reason: filtering the
  // fetched page client-side would leave `total` describing the unfiltered
  // population, so "Showing 1-3 of 312" would be a lie in both halves.
  const search = useDebouncedValue(caseSearch.trim());
  const query = useCaseQuery({
    ...(search ? { q: search } : {}),
    ...(scope === 'mine' ? { assignedToMe: true } : {}),
  });
  // #516 — id → company name. One shared fetch; a row whose company is not in
  // the directory (or a load that failed) falls back to the raw id rather than
  // rendering nothing. The search box already reaches customers server-side, so
  // typing a company id here narrows the queue to that company.
  const directory = useCustomerDirectory();
  // #540 — this analyst's own column choice, or the ten columns this queue has
  // always shown if they have never opened the picker.
  const columns = useQueueColumns('cases');

  // One cell, by column key. The vocabulary and the header labels live in
  // components/QueueColumns.tsx; what a cell CONTAINS is this page's business,
  // which is why the renderer stays here rather than travelling with the label.
  const cell = (key: string, c: Case): ReactNode => {
    switch (key) {
      // #766 — "New": sent to vetting and not yet opened by its OA/VA Agent
      // (vettingOpenedAt, stamped server-side when the agent opens the case),
      // so it is the same for every viewer and clears for everyone at once.
      case 'case':
        return (
          <>
            <button type="button" style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600 }} onClick={e => { e.stopPropagation(); openCase(c, 'vetting'); }}>{c.id}</button>
            {isNewToVetting(c) && (
              <span data-testid="new-badge" style={{ ...styles.badge, background: C.infoBg, color: C.info, marginLeft: 6 }}>New</span>
            )}
          </>
        );
      case 'registrant':
        return c.registrant || <em style={{ color: C.textDim }}>New case</em>;
      // #540 — the CASE RCD, not the submitted intake payload. Staff amend
      // these during vetting, and a queue showing what the customer originally
      // typed would contradict the case's own RCD tab — the one screen an
      // analyst would check after spotting something here.
      case 'displayName':
        return c.rcd?.displayName || <span style={{ color: C.textMuted }}>—</span>;
      case 'callReason':
        return c.rcd?.callReason || <span style={{ color: C.textMuted }}>—</span>;
      case 'customer':
        return <CustomerLabel customerId={caseCustomerId(c)} name={directory.nameOf(caseCustomerId(c))} />;
      case 'status':
        return <StatusBadge status={c.status} />;
      // #766 — the case progress tracker's stage and its first open item.
      case 'stage':
        return <CaseStageCell c={c} />;
      // #693 — the registrant's AUTHORIZER. The column key stays 'oa': it is
      // stored in each user's saved queue layout and validated by an enum in
      // routes/me.ts, so renaming it would silently drop every saved layout
      // that names it. Only what it SHOWS changed.
      case 'oa':
        return <span style={{ fontSize: 12, color: C.textMuted }}>{notificationContactLabel(c)}</span>;
      case 'eid':
        return <span style={{ fontSize: 11, fontFamily: 'monospace', color: c.eid ? C.success : C.textDim }}>{c.eid || '—'}</span>;
      case 'created':
        return fmtDate(c.createdAt);
      case 'sla':
        return <VettingSlaCell c={c} now={now} />;
      // #540 fold-in A — `isNotifiableDecision`, imported from the lifecycle
      // actions rather than re-derived, and not `c.decision` for truthiness.
      // A DELETED case IS decided, so the old truthiness test reported a
      // notification as "⚠ Pending" for a case that has no notice to send and
      // never will; the predicate answers the question this column is actually
      // asking, which is whether a notice exists.
      case 'notification':
        return c.notificationSentAt
          ? <span style={{ color: C.success, fontSize: 12 }}>✓ Sent {fmtDate(c.notificationSentAt)}</span>
          : isNotifiableDecision(c.decision)
            ? <span style={{ color: C.warn, fontSize: 12 }}>⚠ Pending</span>
            : <span style={{ color: C.textDim, fontSize: 12 }}>—</span>;
      case 'settlement':
        return c.settlementSyncStatus ? <StatusBadge status={c.settlementSyncStatus} /> : '—';
      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>Vetting Cases</h2>
        <button style={{ ...styles.btn, ...styles.btnGold }} onClick={async () => {
          try {
            const newCase = await createCase({
              // #583 — ONBOARDING, not IN_REVIEW. This button used to create the
              // case directly in vetting, which put a case with NOTHING filled in
              // on the analyst's queue and started the vetting SLA clock against
              // it, having satisfied none of the onboarding gate's required
              // fields (REQUIRED_FIELDS in CaseDetail/tabs/OnboardingTab.tsx) and
              // no notification contact email — where the decision notice goes
              // (#543). The case now starts where the "+ New Application" button
              // on the Onboarding page starts one, and reaches vetting the same
              // way every other case does: fill the form, Submit Onboarding,
              // Accept. The API refuses the old shape outright, so this is the
              // caller catching up with the rule and not the rule itself.
              //
              // #286 still applies at the far end: the server derives the vetting
              // SLA deadline from the status→IN_REVIEW transition, which is now
              // the Accept, using its own clock.
              status: 'ONBOARDING',
              analystId: user.id,
              action: { code: 'CASE_CREATED', detail: { source: 'cases-list' } },
            });
            addCase(newCase);
            query.refetch();
            openCase(newCase);
          } catch (err) {
            // #583 — `err.message` is the problem+json TITLE, which for a
            // refused create is a bare status phrase. The `detail` is the
            // sentence naming what is wrong; showing the title alone is the raw
            // -400 report this issue asked to be rid of.
            const reason = err instanceof ApiProblem
              ? (err.detail ?? err.title)
              : err instanceof Error ? err.message : String(err);
            showToast(`Failed to create case: ${reason}`);
          }
        }}>+ New Case</button>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input aria-label="Search cases" style={{ ...styles.input, maxWidth: 400 }} placeholder="Search cases by ID, registrant, authorizer, or EID..." value={caseSearch} onChange={e => setCaseSearch(e.target.value)} />
        <select
          aria-label="Case scope"
          style={styles.select}
          value={scope}
          onChange={e => setScope(e.target.value as CaseFilter)}
        >
          <option value="all">All cases</option>
          <option value="mine">Assigned to me</option>
        </select>
        <QueueColumnPicker queue="cases" onError={showToast} />
      </div>
      <div style={styles.card}>
        <table style={styles.table}>
          <thead><tr>
            {columns.map(col => <th key={col.key} style={styles.th}>{col.label}</th>)}
          </tr></thead>
          <tbody>
            {query.cases.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openCase(c, 'vetting')}>
                {columns.map(col => <td key={col.key} style={styles.td}>{cell(col.key, c)}</td>)}
              </tr>
            ))}
            {!query.loading && query.cases.length === 0 && (
              <tr><td colSpan={columns.length} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>
                {search
                  ? `No cases match “${search}”`
                  : scope === 'mine' ? 'No cases are assigned to you' : 'No cases'}
              </td></tr>
            )}
          </tbody>
        </table>
        {query.error && <div style={{ fontSize: 12, color: C.danger, marginTop: 12 }}>Failed to load cases: {query.error}</div>}
        <PagingControls offset={query.offset} limit={query.limit} total={query.total} count={query.cases.length} onOffset={query.setOffset} />
      </div>
    </div>
  );
}
